// SecretAAExpression — AST canonical do SecretAA sở hữu (ADR-003 mục 7, K3). JSONLogic / CEL chỉ là adapter, không bao giờ là
// định dạng lưu. Validator kiểm kiểu theo field, giới hạn kích thước; field RETIRED / không tồn tại ⇒ EXPRESSION_INVALID.
import { z } from "zod";

export const EXPRESSION_LIMITS = { maxDepth: 16, maxNodes: 256, maxList: 100, maxString: 1024 } as const;

const NumberString = z.string().regex(/^-?\d{1,18}(\.\d{1,12})?$/, "số thập phân dạng chuỗi");
const MinorString = z.string().regex(/^-?\d{1,18}$/, "số nguyên minor unit");
const DateString = z.string().regex(/^\d{4}-\d{2}-\d{2}$/, "YYYY-MM-DD");

/** Literal: chuỗi · bool · null · số nguyên an toàn · {number} thập phân · {money, currency} · {date} · danh sách (cho `in`). */
const Scalar = z.union([
  z.string().max(EXPRESSION_LIMITS.maxString),
  z.boolean(),
  z.null(),
  z.number().int().refine(Number.isSafeInteger, "số nguyên an toàn"),
  z.object({ number: NumberString }).strict(),
  z.object({ money: MinorString, currency: z.string().regex(/^[A-Z]{3}$/) }).strict(),
  z.object({ date: DateString }).strict(),
]);
export const Literal = z.union([Scalar, z.array(Scalar).max(EXPRESSION_LIMITS.maxList)]);

export const BINARY_OPS = ["eq", "ne", "lt", "lte", "gt", "gte", "starts_with", "contains", "in", "add", "sub", "mul", "div"] as const;
export const UNARY_OPS = ["not", "is_null"] as const;
export const NARY_OPS = ["and", "or"] as const;

export type Expr =
  | { literal: z.infer<typeof Literal> }
  | { field: string }
  | { var: string }
  | { op: (typeof NARY_OPS)[number]; args: Expr[] }
  | { op: (typeof UNARY_OPS)[number]; arg: Expr }
  | { op: (typeof BINARY_OPS)[number]; left: Expr; right: Expr }
  | { op: "if"; cond: Expr; then: Expr; else: Expr };

export const Expr: z.ZodType<Expr> = z.lazy(() =>
  z.union([
    z.object({ literal: Literal }).strict(),
    z.object({ field: z.string().uuid() }).strict(),
    z.object({ var: z.string().regex(/^[a-z][a-z0-9_.]{0,63}$/) }).strict(),
    z.object({ op: z.enum(NARY_OPS), args: z.array(Expr).min(1).max(50) }).strict(),
    z.object({ op: z.enum(UNARY_OPS), arg: Expr }).strict(),
    z.object({ op: z.enum(BINARY_OPS), left: Expr, right: Expr }).strict(),
    z.object({ op: z.literal("if"), cond: Expr, then: Expr, else: Expr }).strict(),
  ]),
);

/** Kiểu giá trị của một nút sau khi kiểm. `option` = id option select, `ref` = id record (relation). */
export type ExprType = "text" | "number" | "money" | "date" | "bool" | "null" | "option" | "ref";

export interface ExprField {
  readonly id: string;
  readonly type: "text" | "number" | "money" | "date" | "bool" | "select" | "relation";
  readonly status: "active" | "retired" | "purged";
  readonly currency?: string | undefined;
}

export interface ExprContext {
  readonly fields: ReadonlyMap<string, ExprField>;
  /** Biến được phép và kiểu của chúng (ví dụ `today: date` cho filter của view). */
  readonly vars: Readonly<Record<string, ExprType>>;
}

export class ExpressionError extends Error {
  constructor(
    message: string,
    readonly path: string,
  ) {
    super(message);
  }
}

const fieldType = (f: ExprField): ExprType => (f.type === "select" ? "option" : f.type === "relation" ? "ref" : f.type);
const literalType = (v: z.infer<typeof Literal>): ExprType | ExprType[] => {
  if (Array.isArray(v)) return v.map((x) => literalType(x) as ExprType);
  if (v === null) return "null";
  if (typeof v === "string") return "text";
  if (typeof v === "boolean") return "bool";
  if (typeof v === "number") return "number";
  if ("number" in v) return "number";
  if ("money" in v) return "money";
  return "date";
};
/** Chuỗi literal so được với option (id option) / ref (id record) / text. */
const compatible = (a: ExprType, b: ExprType) => a === b || b === "null" || a === "null" || (a === "text" && (b === "option" || b === "ref")) || (b === "text" && (a === "option" || a === "ref"));

interface Typed {
  readonly type: ExprType;
  readonly currency?: string | undefined;
}

/** Kiểm cấu trúc + kiểu + giới hạn. Trả kiểu của biểu thức; sai ⇒ ExpressionError (mã API: EXPRESSION_INVALID). */
export function validateExpression(input: unknown, ctx: ExprContext): { expr: Expr; type: ExprType } {
  const parsed = Expr.safeParse(input);
  if (!parsed.success) throw new ExpressionError(`Biểu thức sai cấu trúc: ${parsed.error.issues[0]?.message ?? "?"}`, parsed.error.issues[0]?.path.join(".") ?? "");
  let nodes = 0;
  const walk = (e: Expr, path: string, depth: number): Typed => {
    nodes += 1;
    if (nodes > EXPRESSION_LIMITS.maxNodes) throw new ExpressionError(`Quá ${EXPRESSION_LIMITS.maxNodes} nút`, path);
    if (depth > EXPRESSION_LIMITS.maxDepth) throw new ExpressionError(`Sâu quá ${EXPRESSION_LIMITS.maxDepth} tầng`, path);
    if ("literal" in e) {
      const t = literalType(e.literal);
      if (Array.isArray(t)) return { type: t[0] ?? "null" };
      const cur = typeof e.literal === "object" && e.literal !== null && !Array.isArray(e.literal) && "currency" in e.literal ? e.literal.currency : undefined;
      return { type: t, currency: cur };
    }
    if ("field" in e) {
      const f = ctx.fields.get(e.field);
      if (!f) throw new ExpressionError("Field không tồn tại trong collection", path);
      if (f.status !== "active") throw new ExpressionError("Field đã ngừng dùng — không dùng trong biểu thức", path);
      return { type: fieldType(f), currency: f.currency };
    }
    if ("var" in e) {
      const t = ctx.vars[e.var];
      if (!t) throw new ExpressionError(`Biến « ${e.var} » không được phép ở đây`, path);
      return { type: t };
    }
    switch (e.op) {
      case "and":
      case "or":
        e.args.forEach((a, i) => {
          if (walk(a, `${path}.args.${i}`, depth + 1).type !== "bool") throw new ExpressionError(`${e.op} cần vế bool`, `${path}.args.${i}`);
        });
        return { type: "bool" };
      case "not":
        if (walk(e.arg, `${path}.arg`, depth + 1).type !== "bool") throw new ExpressionError("not cần vế bool", path);
        return { type: "bool" };
      case "is_null":
        walk(e.arg, `${path}.arg`, depth + 1);
        return { type: "bool" };
      case "if": {
        if (walk(e.cond, `${path}.cond`, depth + 1).type !== "bool") throw new ExpressionError("if cần điều kiện bool", path);
        const a = walk(e.then, `${path}.then`, depth + 1);
        const b = walk(e.else, `${path}.else`, depth + 1);
        if (!compatible(a.type, b.type)) throw new ExpressionError("Hai nhánh if khác kiểu", path);
        return a.type === "null" ? b : a;
      }
      default: {
        const l = walk(e.left, `${path}.left`, depth + 1);
        const r = walk(e.right, `${path}.right`, depth + 1);
        if (e.op === "in") {
          if (!("literal" in e.right) || !Array.isArray(e.right.literal)) throw new ExpressionError("in cần danh sách literal ở vế phải", path);
          for (const t of literalType(e.right.literal) as ExprType[]) if (!compatible(l.type, t)) throw new ExpressionError("Phần tử của in khác kiểu field", path);
          return { type: "bool" };
        }
        if (!compatible(l.type, r.type)) throw new ExpressionError(`Không so / tính được ${l.type} với ${r.type}`, path);
        if (l.type === "money" && r.type === "money" && l.currency && r.currency && l.currency !== r.currency) throw new ExpressionError("Khác loại tiền — không so / tính được", path);
        if (e.op === "starts_with" || e.op === "contains") {
          if (l.type !== "text") throw new ExpressionError(`${e.op} chỉ dùng cho text`, path);
          return { type: "bool" };
        }
        if (["add", "sub", "mul", "div"].includes(e.op)) {
          if (!["number", "money"].includes(l.type)) throw new ExpressionError(`${e.op} chỉ dùng cho number / money`, path);
          if (l.type === "money" && (e.op === "mul" || e.op === "div") && r.type === "money") throw new ExpressionError("Không nhân / chia hai số tiền", path);
          return l;
        }
        if (["lt", "lte", "gt", "gte"].includes(e.op) && !["number", "money", "date", "text"].includes(l.type === "null" ? r.type : l.type)) {
          throw new ExpressionError(`${e.op} không dùng cho ${l.type}`, path);
        }
        return { type: "bool" };
      }
    }
  };
  const t = walk(parsed.data, "", 0);
  return { expr: parsed.data, type: t.type };
}
